/**
 * @version 1.0.0
 * @created 2026-02-02
 * @updated 2026-02-05
 * @description [Service] Supabase Browser Client with cookie-based session management
 *              Uses Supabase SSR for proper cookie handling
 */
import type { SupabaseClient } from "@supabase/supabase-js";
import { createBrowserClient } from "@supabase/ssr";

declare global {
  var SUPABASE_CLIENT: SupabaseClient | undefined;
}

/**
 * Browser Supabase client with cookie-based session management
 * Uses Supabase SSR for proper cookie handling
 *
 * This client:
 * - Reads/writes session cookies (not localStorage)
 * - Syncs with server-side session (createSupabaseServerClient)
 * - Automatically includes cookies in requests
 *
 * Use this for:
 * - OAuth authentication flows
 * - Client-side Supabase operations
 * - Session state management
 *
 * @returns Supabase client instance
 */
export const createCSRClient = (): SupabaseClient => {
  if (!globalThis.SUPABASE_CLIENT) {
    const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

    if (!url || !key) {
      throw new Error("[Browser] Missing required environment variables: NEXT_PUBLIC_SUPABASE_URL, NEXT_PUBLIC_SUPABASE_ANON_KEY");
    }

    globalThis.SUPABASE_CLIENT = createBrowserClient(url, key);
  }

  return globalThis.SUPABASE_CLIENT;
};
